// The "you have already ordered a brief for this business" warning.
//
// When the customer leaves the "Registered legal name" box, the briefs list is
// asked for that name and the answer is checked for an EXACT match. A match is
// a WARNING, never a block: ordering a second brief on the same company is a
// legitimate thing to do — the first one may be months old, or about a
// different deal — so the customer is told and left to decide.
//
// Exact, because the list endpoint's `search` is a substring match. Asking it
// for "Gulf Steel" comes back with "Gulf Steel Trading FZE" AND "Gulf Steel
// Holdings LLC"; warning on either would cry wolf on almost every name.
//
// No React and no store here, so the matching can be pinned by a test.

/**
 * The two names are compared with the noise a human types stripped out:
 * surrounding and repeated whitespace, and case. Nothing else — no company
 * suffix stripping, so "Gulf Steel Trading" and "Gulf Steel Trading FZE" stay
 * different businesses, which they are.
 */
export const normaliseName = (value) =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ").toLowerCase() : "";

/** Whether a name is worth asking the backend about at all. */
export const isSearchable = (value) => normaliseName(value).length > 0;

/**
 * The briefs whose subject is exactly this name.
 *
 * Rows come from the list mapping, so the subject is on `company`.
 */
export const matchingBriefs = (rows, name) => {
  const target = normaliseName(name);
  if (!target) return [];
  return (Array.isArray(rows) ? rows : []).filter(
    (row) => normaliseName(row?.company) === target
  );
};

export const DUPLICATE_COPY = {
  one: (name) => `You already have a brief for ${name}. You can still order another one.`,
  many: (name, count) =>
    `You already have ${count} briefs for ${name}. You can still order another one.`,
};

/**
 * The sentence to print under the box, or null when there is nothing to say.
 *
 * The name in the sentence is the one already ON FILE, not the one just typed:
 * they differ only in case and spacing, and showing the stored spelling is what
 * lets the customer recognise the brief they already have.
 */
export const duplicateWarning = (rows, name) => {
  const matches = matchingBriefs(rows, name);
  if (matches.length === 0) return null;

  const onFile = matches[0].company || name;
  return matches.length === 1
    ? DUPLICATE_COPY.one(onFile)
    : DUPLICATE_COPY.many(onFile, matches.length);
};

export default duplicateWarning;
